// /src/VersionContext.js

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import { versionSocket } from "../utils/Socket";
import { registerSocket } from "./wsRecovery/wsResume";
import { startHeartbeat } from "./wsRecovery/heartbeat";
import {
  getStoredVersion,
  setStoredVersion,
  getRunningVersion,
  fetchDeployedVersion,
  pickRemoteVersion,
} from "../utils/frontendVersionCheck";
import { finalizePendingVersionAck } from "../utils/ensureFreshFrontendBundle";

const VersionContext = createContext();
export const useVersion = () => useContext(VersionContext);

export function VersionProvider({ children }) {
  const [currentVersion, setCurrentVersion] = useState(null);
  const [pendingVersion, setPendingVersion] = useState(null);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [updateLocked, setUpdateLocked] = useState(false);
  const updateLockedRef = useRef(false);
  const pendingVersionRef = useRef(null);

  updateLockedRef.current = updateLocked;
  pendingVersionRef.current = pendingVersion;

  const offerUpdate = useCallback((remoteVersion) => {
    if (!remoteVersion) return;

    if (updateLockedRef.current) {
      if (pendingVersionRef.current === remoteVersion) return;
      setUpdateLocked(false);
    }

    const local = getStoredVersion();
    const running = getRunningVersion();

    const localOutdated = Boolean(local && remoteVersion !== local);
    const bundleOutdated = Boolean(running && remoteVersion !== running);

    if (bundleOutdated && running) {
      setPendingVersion(remoteVersion);
      setHasUpdate(true);
      setUpdateLocked(true);
      return;
    }

    if (!localOutdated && !bundleOutdated) {
      finalizePendingVersionAck(remoteVersion);
      if (running) {
        setStoredVersion(running);
        setCurrentVersion(running);
      } else if (local) {
        setCurrentVersion(local);
      }
      return;
    }

    setPendingVersion(remoteVersion);
    setHasUpdate(true);
    setUpdateLocked(true);
  }, []);

  const resolveRemoteVersion = useCallback(async (wsVersion) => {
    const deployed = await fetchDeployedVersion();
    return pickRemoteVersion(deployed, wsVersion);
  }, []);

  const syncVersionFromServer = useCallback(async () => {
    try {
      const [apiRes, deployed] = await Promise.all([
        axios.get("/api/system/frontend-version"),
        fetchDeployedVersion(),
      ]);

      const remote = pickRemoteVersion(
        deployed,
        apiRes.data?.version || null,
      );
      if (!remote) return;

      const local = getStoredVersion();
      if (!local) {
        const running = getRunningVersion();
        if (running && remote !== running) {
          offerUpdate(remote);
          return;
        }
        setStoredVersion(remote);
        setCurrentVersion(remote);
        return;
      }

      offerUpdate(remote);
    } catch {
      // ignore — WS hello may still deliver state
    }
  }, [offerUpdate]);

  const resumeVersionChannel = useCallback(() => {
    const sock = versionSocket;
    if (!sock.connected) {
      sock.connect();
    } else {
      sock.emit("version:hello");
    }
    syncVersionFromServer();
  }, [syncVersionFromServer]);

  // REST bootstrap on mount
  useEffect(() => {
    syncVersionFromServer();
  }, [syncVersionFromServer]);

  // Wake / network / visibility — tab open for hours without full reload
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        resumeVersionChannel();
      }
    };

    window.addEventListener("online", resumeVersionChannel);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.removeEventListener("online", resumeVersionChannel);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [resumeVersionChannel]);

  // Global sleepyTick resume (after initWsRecovery in wsRecovery/index.js)
  useEffect(() => {
    registerSocket("version", resumeVersionChannel);
    return () => registerSocket("version", () => {});
  }, [resumeVersionChannel]);

  // WS — hello on every connect (including socket.io reconnect after sleep)
  useEffect(() => {
    const sock = versionSocket;

    startHeartbeat(sock);

    const hello = () => sock.emit("version:hello");

    const onState = async ({ version }) => {
      const remote = await resolveRemoteVersion(version);
      offerUpdate(remote);
    };

    const onUpdate = async ({ version }) => {
      const remote = await resolveRemoteVersion(version);
      offerUpdate(remote);
    };

    const onConnect = () => {
      hello();
      syncVersionFromServer();
    };

    sock.on("connect", onConnect);
    sock.on("version:state", onState);
    sock.on("version:update", onUpdate);

    if (sock.connected) {
      hello();
    }

    return () => {
      sock.off("connect", onConnect);
      sock.off("version:state", onState);
      sock.off("version:update", onUpdate);
    };
  }, [offerUpdate, resolveRemoteVersion, syncVersionFromServer]);

  const acknowledge = () => {
    if (!pendingVersion) return;

    setStoredVersion(pendingVersion);
    setCurrentVersion(pendingVersion);
    setPendingVersion(null);
    setHasUpdate(false);
    setUpdateLocked(false);
  };

  return (
    <VersionContext.Provider value={{ currentVersion, hasUpdate, pendingVersion, acknowledge }}>
      {children}
    </VersionContext.Provider>
  );
}
